import {
	DisruptionAlert,
	NotificationRollset,
} from "@/pages/rollshop/models/RollshopNotification";
import {
	Box,
	Card,
	CardContent,
	Collapse,
	Typography,
	alpha,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { CardHeader } from "../NotificationDetails";
import { OrphanBadge } from "./OrphanBadge";
import { RollBox } from "./RollBox";
import { ORPHAN_COLOR, ROLL_COLORS } from "./RollColors";
import { UnassignedRollBox } from "./UnassignedRollBox";

const orphanRollNames = (alerts: DisruptionAlert[]) => {
	return alerts
		.filter((a) => a.AlertTypeCode === "R" && a.OrphanedRollCount > 0)
		.flatMap((a) => a.OrphanedRollNames?.split(",").map((r) => r.trim()) || [])
		.filter((roll, index, self) => roll && self.indexOf(roll) === index);
};

const getColor = (
	rollName: string | null,
	rollColorMap: Record<
		string,
		{
			bg: string;
			border: string;
			text: string;
			line: string;
		}
	>,
) => {
	if (!rollName) return undefined;
	return rollColorMap[rollName];
};

type RollConnection = {
	rollName: string;
	fromRollsetId: number | null;
	fromPosition: "TR" | "BR" | null;
	toRollsetId: number | null;
	toPosition: "TR" | "BR" | null;
	isOrphan: boolean;
	isUnassigned: boolean;
};

type Props = {
	targetRollsets: NotificationRollset[];
	brokenRollsets: NotificationRollset[];
	alerts: DisruptionAlert[];
};

export const HistoryDiagram = ({
	targetRollsets,
	brokenRollsets,
	alerts,
}: Props) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const rollRefs = useRef<Map<string, React.RefObject<HTMLDivElement | null>>>(
		new Map(),
	);

	const [expanded, setExpanded] = useState(true);
	const [arrowPaths, setArrowPaths] = useState<
		Array<{
			path: string;
			labelX: number;
			labelY: number;
			color: string;
			rollName: string;
			isOrphan: boolean;
			index: number;
		}>
	>([]);

	// Collect all unique roll names and assign colors
	const rollColorMap = useMemo(() => {
		const allRolls = new Set<string>();

		for (const rs of brokenRollsets) {
			if (rs.TopRollName) allRolls.add(rs.TopRollName);
			if (rs.BottomRollName) allRolls.add(rs.BottomRollName);
		}
		for (const rs of targetRollsets) {
			if (rs.TopRollName) allRolls.add(rs.TopRollName);
			if (rs.BottomRollName) allRolls.add(rs.BottomRollName);
		}

		const colorMap: Record<string, (typeof ROLL_COLORS)[0]> = {};
		Array.from(allRolls).forEach((rollName, idx) => {
			colorMap[rollName] = ROLL_COLORS[idx % ROLL_COLORS.length];
		});

		return colorMap;
	}, [brokenRollsets, targetRollsets]);

	// Build connections
	const { connections, unassignedRolls, orphansByPosition } = useMemo(() => {
		const brokenRollNames = new Set<string>();
		for (const rs of brokenRollsets) {
			if (rs.TopRollName) brokenRollNames.add(rs.TopRollName);
			if (rs.BottomRollName) brokenRollNames.add(rs.BottomRollName);
		}

		const conns: RollConnection[] = [];

		const findTargetForRoll = (
			rollName: string,
		): { rollsetId: number; position: "TR" | "BR" } | null => {
			for (const target of targetRollsets) {
				if (target.TopRollName === rollName) {
					return { rollsetId: target.NotificationRollsetId, position: "TR" };
				}
				if (target.BottomRollName === rollName) {
					return { rollsetId: target.NotificationRollsetId, position: "BR" };
				}
			}
			return null;
		};

		// Connections from broken rollsets
		for (const rs of brokenRollsets) {
			if (rs.TopRollName) {
				const isOrphan = orphanRollNames(alerts).includes(rs.TopRollName);
				const targetInfo = isOrphan ? null : findTargetForRoll(rs.TopRollName);

				conns.push({
					rollName: rs.TopRollName,
					fromRollsetId: rs.NotificationRollsetId,
					fromPosition: "TR",
					toRollsetId: targetInfo?.rollsetId || null,
					toPosition: targetInfo?.position || null,
					isOrphan,
					isUnassigned: false,
				});
			}

			if (rs.BottomRollName) {
				const isOrphan = orphanRollNames(alerts).includes(rs.BottomRollName);
				const targetInfo = isOrphan
					? null
					: findTargetForRoll(rs.BottomRollName);

				conns.push({
					rollName: rs.BottomRollName,
					fromRollsetId: rs.NotificationRollsetId,
					fromPosition: "BR",
					toRollsetId: targetInfo?.rollsetId || null,
					toPosition: targetInfo?.position || null,
					isOrphan,
					isUnassigned: false,
				});
			}
		}

		// Unassigned rolls
		const unassigned: {
			rollName: string;
			targetRollsetId: number;
			position: "TR" | "BR";
		}[] = [];

		for (const target of targetRollsets) {
			if (target.TopRollName && !brokenRollNames.has(target.TopRollName)) {
				unassigned.push({
					rollName: target.TopRollName,
					targetRollsetId: target.NotificationRollsetId,
					position: "TR",
				});
				conns.push({
					rollName: target.TopRollName,
					fromRollsetId: null,
					fromPosition: null,
					toRollsetId: target.NotificationRollsetId,
					toPosition: "TR",
					isOrphan: false,
					isUnassigned: true,
				});
			}
			if (
				target.BottomRollName &&
				!brokenRollNames.has(target.BottomRollName)
			) {
				unassigned.push({
					rollName: target.BottomRollName,
					targetRollsetId: target.NotificationRollsetId,
					position: "BR",
				});
				conns.push({
					rollName: target.BottomRollName,
					fromRollsetId: null,
					fromPosition: null,
					toRollsetId: target.NotificationRollsetId,
					toPosition: "BR",
					isOrphan: false,
					isUnassigned: true,
				});
			}
		}

		// Group orphans
		const orphansMap = new Map<string, RollConnection>();
		for (const conn of conns.filter((c) => c.isOrphan)) {
			const key = `${conn.fromRollsetId}-${conn.fromPosition}`;
			orphansMap.set(key, conn);
		}

		return {
			connections: conns,
			unassignedRolls: unassigned,
			orphansByPosition: orphansMap,
		};
	}, [brokenRollsets, targetRollsets, orphanRollNames]);

	// Get or create ref
	const getRef = (key: string) => {
		if (!rollRefs.current.has(key)) {
			rollRefs.current.set(key, { current: null });
		}
		return rollRefs.current.get(key)!;
	};

	// Calculate arrow paths
	const calculateArrows = () => {
		if (!containerRef.current) return;

		const containerRect = containerRef.current.getBoundingClientRect();
		const newPaths: typeof arrowPaths = [];

		connections.forEach((conn, idx) => {
			let fromRef: React.RefObject<HTMLDivElement | null> | undefined;
			let toRef: React.RefObject<HTMLDivElement | null> | undefined;

			if (conn.isUnassigned) {
				fromRef = rollRefs.current.get(
					`unassigned-${conn.toRollsetId}-${conn.toPosition}`,
				);
				toRef = rollRefs.current.get(
					`target-${conn.toRollsetId}-${conn.toPosition}`,
				);
			} else if (conn.isOrphan) {
				fromRef = rollRefs.current.get(
					`broken-${conn.fromRollsetId}-${conn.fromPosition}`,
				);
				toRef = rollRefs.current.get(
					`orphan-${conn.fromRollsetId}-${conn.fromPosition}`,
				);
			} else if (conn.toRollsetId) {
				fromRef = rollRefs.current.get(
					`broken-${conn.fromRollsetId}-${conn.fromPosition}`,
				);
				toRef = rollRefs.current.get(
					`target-${conn.toRollsetId}-${conn.toPosition}`,
				);
			}

			if (!fromRef?.current || !toRef?.current) return;

			const fromRect = fromRef.current.getBoundingClientRect();
			const toRect = toRef.current.getBoundingClientRect();

			const startX = fromRect.right - containerRect.left + 2;
			const startY = fromRect.top + fromRect.height / 2 - containerRect.top;
			const endX = toRect.left - containerRect.left - 2;
			const endY = toRect.top + toRect.height / 2 - containerRect.top;

			const dx = endX - startX;
			const controlOffset = Math.min(dx * 0.4, 60);

			// Use straight line for orphan connections and curved for others
			let path: string;
			if (conn.isOrphan) {
				// Line from the center of the orphan roll to the OrphanBadge
				// If they're at different Y positions, use a simple connecting line
				if (Math.abs(startY - endY) < 5) {
					// Nearly aligned - use straight horizontal line
					path = `M ${startX} ${startY} L ${endX} ${endY}`;
				} else {
					// Different heights - use rectangular path: horizontal -> vertical -> horizontal
					const midX = startX + 30;
					path = `M ${startX} ${startY} L ${midX} ${startY} L ${midX} ${endY} L ${endX} ${endY}`;
				}
			} else {
				// Curved path for normal connections
				path = `M ${startX} ${startY} C ${startX + controlOffset} ${startY}, ${
					endX - controlOffset
				} ${endY}, ${endX} ${endY}`;
			}

			const labelX = (startX + endX) / 2;
			const labelY = (startY + endY) / 2;

			const color = conn.isOrphan
				? ORPHAN_COLOR.line
				: getColor(conn.rollName, rollColorMap)?.line || "#9ca3af";

			newPaths.push({
				path,
				labelX,
				labelY,
				color,
				rollName: conn.rollName,
				isOrphan: conn.isOrphan,
				index: idx,
			});
		});

		setArrowPaths(newPaths);
	};

	useEffect(() => {
		const timer = setTimeout(calculateArrows, 100);
		return () => clearTimeout(timer);
	}, [brokenRollsets, targetRollsets, alerts, connections]);

	// Recalculate on resize
	useEffect(() => {
		if (!containerRef.current) return;

		const resizeObserver = new ResizeObserver(() => {
			calculateArrows();
		});

		resizeObserver.observe(containerRef.current);
		return () => resizeObserver.disconnect();
	}, [connections]);

	if (brokenRollsets.length === 0 && targetRollsets.length === 0) {
		return null;
	}

	return (
		<Card
			elevation={0}
			sx={{
				backgroundColor: "background.paper",
				borderRadius: 2.5,
				border: `1px solid`,
				borderColor: alpha("#6366f1", 0.15),
				overflow: "hidden",
			}}
		>
			<CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
				<CardHeader
					label="History"
					backgroundColor="#6366f1"
					expanded={expanded}
					setExpanded={setExpanded}
				/>

				<Collapse in={expanded} timeout="auto">
					<Box
						ref={containerRef}
						sx={{
							position: "relative",
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							p: 2,
							mt: 2,
							backgroundColor: alpha("#6366f1", 0.02),
							borderRadius: 2,
							border: `1px solid ${alpha("#6366f1", 0.08)}`,
							minHeight: 150,
						}}
					>
						{/* Left Column: Broken Rollsets + Unassigned rolls */}
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								zIndex: 1,
								position: "relative",
								alignItems: "center",
							}}
						>
							{/* Broken rollsets with orphan badges */}
							<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
								{brokenRollsets.map((rs) => {
									const topOrphan = orphansByPosition.get(
										`${rs.NotificationRollsetId}-TR`,
									);
									const bottomOrphan = orphansByPosition.get(
										`${rs.NotificationRollsetId}-BR`,
									);

									return (
										<Box
											key={rs.NotificationRollsetId}
											sx={{ display: "flex", alignItems: "stretch", gap: 6 }}
										>
											<Box
												sx={{
													border: `2px solid ${alpha("#ef4444", 0.4)}`,
													borderRadius: 2,
													overflow: "hidden",
													minWidth: 110,
													backgroundColor: "background.paper",
													boxShadow: `0 2px 8px ${alpha("#ef4444", 0.1)}`,
												}}
											>
												<Box
													sx={{
														backgroundColor: alpha("#ef4444", 0.08),
														px: 1.5,
														py: 0.75,
														borderBottom: `1px solid ${alpha("#ef4444", 0.15)}`,
													}}
												>
													<Typography
														sx={{
															fontWeight: 700,
															fontSize: "0.7rem",
															color: "error.main",
															fontFamily: "monospace",
															textAlign: "center",
														}}
													>
														{rs.SectionName} - {rs.StandName} -{" "}
														{rs.RollsetLetter}
													</Typography>
												</Box>

												<Box
													sx={{
														p: 1,
														display: "flex",
														flexDirection: "column",
														gap: 0.75,
													}}
												>
													<RollBox
														rollName={rs.TopRollName}
														position="TR"
														rollRef={getRef(
															`broken-${rs.NotificationRollsetId}-TR`,
														)}
														color={getColor(rs.TopRollName, rollColorMap)}
													/>
													<RollBox
														rollName={rs.BottomRollName}
														position="BR"
														rollRef={getRef(
															`broken-${rs.NotificationRollsetId}-BR`,
														)}
														color={getColor(rs.BottomRollName, rollColorMap)}
													/>
												</Box>
											</Box>

											{/* Orphan badges aligned with rolls */}
											{(topOrphan || bottomOrphan) && (
												<Box
													sx={{
														display: "flex",
														flexDirection: "column",
														gap: 0,
														pt: 1,
														pb: 1,
													}}
												>
													{topOrphan ? (
														<Box
															sx={{
																display: "flex",
																alignItems: "center",
																height: 44,
																zIndex: 11,
															}}
														>
															<OrphanBadge
																badgeRef={getRef(
																	`orphan-${rs.NotificationRollsetId}-TR`,
																)}
															/>
														</Box>
													) : (
														<Box sx={{ height: 44 }} />
													)}
													{bottomOrphan ? (
														<Box
															sx={{
																display: "flex",
																alignItems: "center",
																height: 44,
																zIndex: 11,
															}}
														>
															<OrphanBadge
																badgeRef={getRef(
																	`orphan-${rs.NotificationRollsetId}-BR`,
																)}
															/>
														</Box>
													) : (
														<Box sx={{ height: 44 }} />
													)}
												</Box>
											)}
										</Box>
									);
								})}
							</Box>

							{/* Unassigned rolls */}
							{unassignedRolls.length > 0 && brokenRollsets.length === 0 && (
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										pt: 3.5,
										gap: 0.75,
									}}
								>
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											minHeight: 44,
										}}
									>
										{unassignedRolls
											.filter((r) => r.position === "TR")
											.map((r) => (
												<UnassignedRollBox
													key={r.rollName}
													rollName={r.rollName}
													badgeRef={getRef(
														`unassigned-${r.targetRollsetId}-TR`,
													)}
													color={getColor(r.rollName, rollColorMap)}
												/>
											))}
									</Box>
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											minHeight: 44,
										}}
									>
										{unassignedRolls
											.filter((r) => r.position === "BR")
											.map((r) => (
												<UnassignedRollBox
													key={r.rollName}
													rollName={r.rollName}
													badgeRef={getRef(
														`unassigned-${r.targetRollsetId}-BR`,
													)}
													color={getColor(r.rollName, rollColorMap)}
												/>
											))}
									</Box>
								</Box>
							)}

							{/* Unassigned rolls when there are broken rollsets */}
							{unassignedRolls.length > 0 && brokenRollsets.length > 0 && (
								<Box
									sx={{
										flex: 1,
										display: "flex",
										flexDirection: "column",
										justifyContent: "flex-end",
										gap: 1,
										mt: 2,
									}}
								>
									<Typography
										sx={{
											fontSize: "0.6rem",
											fontWeight: 600,
											color: "text.secondary",
											textTransform: "uppercase",
											letterSpacing: "0.03em",
											mb: 0.5,
										}}
									>
										Unassigned
									</Typography>
									{unassignedRolls.map((r) => (
										<UnassignedRollBox
											key={r.rollName}
											rollName={r.rollName}
											badgeRef={getRef(
												`unassigned-${r.targetRollsetId}-${r.position}`,
											)}
											color={getColor(r.rollName, rollColorMap)}
										/>
									))}
								</Box>
							)}
						</Box>

						{/* Right Column: Target Rollsets */}
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 2,
								zIndex: 1,
								position: "relative",
								alignItems: "center",
							}}
						>
							{targetRollsets.map((rs) => (
								<Box
									key={rs.NotificationRollsetId}
									sx={{
										border: `2px solid ${alpha("#10b981", 0.4)}`,
										borderRadius: 2,
										overflow: "hidden",
										minWidth: 110,
										backgroundColor: "background.paper",
										boxShadow: `0 2px 8px ${alpha("#10b981", 0.1)}`,
									}}
								>
									<Box
										sx={{
											backgroundColor: alpha("#10b981", 0.08),
											px: 1.5,
											py: 0.75,
											borderBottom: `1px solid ${alpha("#10b981", 0.15)}`,
										}}
									>
										<Typography
											sx={{
												fontWeight: 700,
												fontSize: "0.7rem",
												color: "success.main",
												fontFamily: "monospace",
												textAlign: "center",
											}}
										>
											{rs.SectionName} - {rs.StandName} - {rs.RollsetLetter}
										</Typography>
									</Box>

									<Box
										sx={{
											p: 1,
											display: "flex",
											flexDirection: "column",
											gap: 0.75,
										}}
									>
										<RollBox
											rollName={rs.TopRollName}
											position="TR"
											rollRef={getRef(`target-${rs.NotificationRollsetId}-TR`)}
											color={getColor(rs.TopRollName, rollColorMap)}
										/>
										<RollBox
											rollName={rs.BottomRollName}
											position="BR"
											rollRef={getRef(`target-${rs.NotificationRollsetId}-BR`)}
											color={getColor(rs.BottomRollName, rollColorMap)}
										/>
									</Box>
								</Box>
							))}
						</Box>

						{/* SVG Layer for arrows */}
						<Box
							component="svg"
							sx={{
								position: "absolute",
								top: 0,
								left: 0,
								width: "100%",
								height: "100%",
								pointerEvents: "none",
								overflow: "visible",
								zIndex: 10,
							}}
						>
							<defs>
								{Object.entries(rollColorMap).map(([rollName, color]) => (
									<marker
										key={rollName}
										id={`arr-${rollName.replace(/[^a-zA-Z0-9]/g, "-")}`}
										markerWidth="8"
										markerHeight="6"
										refX="7"
										refY="3"
										orient="auto"
									>
										<polygon points="0 0, 8 3, 0 6" fill={color.line} />
									</marker>
								))}
								<marker
									id="arr-orphan"
									markerWidth="8"
									markerHeight="6"
									refX="7"
									refY="3"
									orient="auto"
								>
									<polygon points="0 0, 8 3, 0 6" fill="#d97706" />
								</marker>
							</defs>

							{arrowPaths.map((arrow, idx) => {
								const markerId = arrow.isOrphan
									? "arr-orphan"
									: `arr-${arrow.rollName.replace(/[^a-zA-Z0-9]/g, "-")}`;

								return (
									<g key={`arrow-${idx}`}>
										{/* Arrow path */}
										<path
											d={arrow.path}
											fill="none"
											stroke={arrow.color}
											strokeWidth="2.5"
											strokeDasharray={arrow.isOrphan ? "6,4" : "none"}
											markerEnd={`url(#${markerId})`}
											style={{
												filter: `drop-shadow(0 1px 2px rgba(0,0,0,0.15))`,
											}}
										/>
									</g>
								);
							})}
						</Box>
					</Box>
				</Collapse>
			</CardContent>
		</Card>
	);
};
